/**
 * P12 — Global Command Bar (⌘K / Ctrl+K).
 *
 * - Opens with Ctrl+K (Win/Linux) or Cmd+K (Mac), or via openCommandBar() context.
 * - Closes on Esc / outside click.
 * - Bilingual fuzzy search powered by /api/command-bar/search.
 * - Keyboard navigation: ↑ / ↓ / Enter.
 * - Recent commands (synced to backend) shown when the query is empty.
 * - Three action types: navigate, navigate-action (?action=…), api.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import * as Icons from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const CommandBarContext = createContext({ open: () => {}, close: () => {} });

export const useCommandBar = () => useContext(CommandBarContext);

const DEBOUNCE_MS = 120;

const Icon = ({ name, className }) => {
  const Comp = (name && Icons[name]) || Icons.Command;
  return <Comp className={className} />;
};

const CommandBarModal = ({ onClose, currentPath }) => {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [recents, setRecents] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const lang = (typeof document !== 'undefined' && document.documentElement.lang) === 'ar' ? 'ar' : 'en';

  // Focus input on mount
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, []);

  // Initial load: recents
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/command-bar/recent');
        if (!cancelled) setRecents(data.items || []);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced search
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.post('/command-bar/search', {
          query, context_path: currentPath, limit: 20,
        });
        if (!cancelled) {
          setItems(data.items || []);
          setActive(0);
        }
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          toast.error(err?.response?.data?.detail || 'Search failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, currentPath]);

  const visible = useMemo(() => {
    if (!query.trim() && recents.length > 0) return recents;
    return items;
  }, [query, items, recents]);

  const execute = useCallback(async (cmd) => {
    if (!cmd) return;
    // Record recent (fire and forget)
    axiosInstance.post('/command-bar/recent', { command_id: cmd.id }).catch(() => {});

    const action = cmd.action || {};
    onClose();
    if (action.type === 'navigate') {
      navigate(action.to);
    } else if (action.type === 'navigate-action') {
      const sep = action.to.includes('?') ? '&' : '?';
      navigate(`${action.to}${sep}action=${action.param || 'create'}`);
    } else if (action.type === 'api') {
      const method = (action.method || 'GET').toUpperCase();
      const path = action.endpoint.replace(/^\/api/, '');
      try {
        toast.loading(cmd.label_en, { id: cmd.id });
        if (method === 'GET') {
          await axiosInstance.get(path);
        } else {
          await axiosInstance.post(path, {});
        }
        toast.success(action.success || `${cmd.label_en} ✓`, { id: cmd.id });
        if (action.redirect) navigate(action.redirect);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Action failed', { id: cmd.id });
      }
    }
  }, [navigate, onClose]);

  // Keyboard nav
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      execute(visible[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll active into view
  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm"
      onMouseDown={onClose}
      data-testid="command-bar-overlay"
    >
      <div
        className="w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="command-bar-modal"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Icons.Search className="w-5 h-5 text-gray-400 flex-none" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={lang === 'ar' ? 'ابحث أو اكتب أمراً…' : 'Search or type a command…'}
            className="flex-1 bg-transparent outline-none text-base placeholder:text-gray-400"
            data-testid="command-bar-input"
          />
          <kbd className="hidden sm:inline px-1.5 py-0.5 rounded border border-gray-200 text-[10px] text-gray-500 font-mono">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto" data-testid="command-bar-results">
          {!query.trim() && recents.length > 0 && (
            <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider text-gray-400">
              {lang === 'ar' ? 'الأوامر الأخيرة' : 'Recent'}
            </div>
          )}
          {visible.length === 0 && !loading && (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="command-bar-empty">
              {lang === 'ar' ? 'لا توجد نتائج' : 'No matches.'}
            </div>
          )}
          {visible.map((cmd, idx) => {
            const label = lang === 'ar' ? cmd.label_ar : cmd.label_en;
            const cat = lang === 'ar' ? cmd.category_ar : cmd.category;
            const isActive = idx === active;
            return (
              <button
                key={cmd.id}
                data-cmd-idx={idx}
                data-testid={`command-item-${cmd.id}`}
                onMouseEnter={() => setActive(idx)}
                onClick={() => execute(cmd)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  isActive ? 'bg-indigo-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className={`p-1.5 rounded-md ${isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                  <Icon name={cmd.icon} className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">{label}</div>
                  <div className="text-[11px] text-gray-500 truncate">{cat}</div>
                </div>
                {cmd.action?.type === 'api' && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                    {lang === 'ar' ? 'تنفيذ' : 'Run'}
                  </span>
                )}
                {cmd.action?.type === 'navigate-action' && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                    {lang === 'ar' ? 'إنشاء' : 'New'}
                  </span>
                )}
                {isActive && (
                  <Icons.CornerDownLeft className="w-3.5 h-3.5 text-indigo-700" />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between text-[11px] text-gray-500">
          <div className="flex items-center gap-3">
            <span><kbd className="px-1 py-0.5 rounded border border-gray-200 font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="px-1 py-0.5 rounded border border-gray-200 font-mono">↵</kbd> select</span>
            <span><kbd className="px-1 py-0.5 rounded border border-gray-200 font-mono">Esc</kbd> close</span>
          </div>
          <span data-testid="command-bar-status">{loading ? '…' : `${visible.length}`}</span>
        </div>
      </div>
    </div>
  );
};

export const CommandBarProvider = ({ children }) => {
  const [opened, setOpened] = useState(false);

  const open = useCallback(() => setOpened(true), []);
  const close = useCallback(() => setOpened(false), []);

  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpened((v) => !v);
        return;
      }
      if (e.key === 'Escape') {
        setOpened((v) => {
          if (v) e.preventDefault();
          return false;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(() => ({ open, close, opened }), [open, close, opened]);

  return (
    <CommandBarContext.Provider value={value}>
      {children}
      {opened && (
        <CommandBarModal
          onClose={close}
          currentPath={typeof window !== 'undefined' ? window.location?.pathname : null}
        />
      )}
    </CommandBarContext.Provider>
  );
};

/** Small floating launcher button (optional embed in topbar). */
export const CommandBarLauncher = ({ className = '' }) => {
  const { open } = useCommandBar();
  return (
    <button
      onClick={open}
      data-testid="command-bar-launcher"
      className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-xs text-gray-500 ${className}`}
    >
      <Icons.Search className="w-3.5 h-3.5" />
      <span>Search / commands…</span>
      <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-200 font-mono text-[10px]">⌘K</kbd>
    </button>
  );
};

export default CommandBarProvider;
